/**
 * The three failure shapes the client can produce, and the one function that turns any of them
 * into a translation key.
 *
 * The API never sends a translated sentence: it sends a stable machine-readable `code`, and the
 * frontend renders `errors.code.<camelCase(code)>` (00-contracts §12.3). `error.message` is
 * English, for developers and logs, and is never shown to a user.
 */
import { errorCodeI18nKey, isErrorCode, type ErrorCode, type ErrorDetail } from '@tvitvi/shared';

import { SupabaseAuthError } from '@/auth/auth-context';
import { i18n } from '@/i18n';
import { dynamicKey, type TKey } from '@/i18n/types';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly requestId: string;
  readonly details: readonly ErrorDetail[];

  constructor(
    status: number,
    code: ErrorCode,
    message: string,
    requestId: string,
    details: readonly ErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }

  /** Field-level errors, derived from `details[].path` (§12.3). */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details) {
      if (detail.path === 'action' || detail.path.length === 0) continue;
      out[detail.path] ??= detail.code;
    }
    return out;
  }
}

/** The request never reached the API: offline, DNS, CORS, aborted connection. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('The API could not be reached.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * The API answered, but not in the shape `@tvitvi/shared` declares. This is always a bug —
 * a deployed frontend older than the API, or a DTO changed without changing the contract.
 */
export class ContractViolationError extends Error {
  readonly path: string;

  constructor(path: string, cause: unknown) {
    super(`Response from ${path} does not match its contract.`);
    this.name = 'ContractViolationError';
    this.path = path;
    this.cause = cause;
  }
}

/**
 * De fire innloggingsfeilene som har sitt eget svar (QA12 #2).
 *
 * Alt annet fra GoTrue faller tilbake til den generiske meldingen, som før. Lista er kort med
 * vilje: en setning per ting brukeren faktisk kan gjøre noe med, og ingen som røper om adressen
 * finnes — «feil e-post ELLER passord» er det samme svaret for begge.
 */
const AUTH_MESSAGE: Record<string, TKey> = {
  invalid_credentials: 'errors.auth.invalidCredentials',
  email_not_confirmed: 'errors.auth.emailNotConfirmed',
  over_request_rate_limit: 'errors.auth.tooManyAttempts',
  over_email_send_rate_limit: 'errors.auth.tooManyAttempts',
  weak_password: 'errors.auth.weakPassword',
  user_already_exists: 'errors.auth.userExists',
};

/**
 * Detaljkoder som har sin egen setning overalt, også i toasten fra `MutationCache`. Ellers sa et
 * avtaleark med signert kontrakt «Skjemaet har feil som må rettes» om «Trekk budet» (røyktest dev 23.10).
 */
const DETAIL_MESSAGE: Readonly<Record<string, TKey>> = {
  deal_sheet_signed: 'errors.code.dealSheetSigned',
  /* QA15 funn 17: forespørsel, bud og motbud gjelder en dato som ikke har vært. */
  date_in_past: 'errors.code.dateInPast',
  /* 276.20 b (1060): i en delt tråd legger hver side bare til egne kolleger. */
  not_a_colleague: 'errors.code.notAColleague',
  /* A8 (1069): honoraret og vilkårene er felles med motparten og endres i avtalen. */
  agreement_shared: 'errors.code.agreementShared',
  /* §8.1 #3: regelen står i API-et nå; skjemaet stopper det samme før det sendes. */
  task_start_after_due: 'errors.code.taskStartAfterDue',
  /* §8.1 #1, #2, #4: reglene fra budmodalen og avtalearket står i API-et også. */
  payment_plan_sum: 'errors.code.paymentPlanSum',
  duplicate_term_label: 'errors.code.duplicateTermLabel',
  amount_must_be_positive: 'errors.code.amountMustBePositive',
  /* Persontypene §3.2 (1097): kontaktpersonen retter de ansatte bare fram til fristen. */
  supplier_deadline_passed: 'errors.code.supplierDeadlinePassed',
  /* Persontypene §3.4.1: en som venter på godkjenning kan ikke få vakt (`approval-gate.ts`). */
  volunteer_pending: 'errors.code.volunteerPending',
  /* Persontypene §2.1, §2.2, §2.4 (1080–1086): sperren på artisttilgangen, fra basen og API-et. */
  artist_share_agency_only: 'errors.code.artistShareAgencyOnly',
  artist_share_organization_agency_only: 'errors.code.artistShareOrganizationAgencyOnly',
  artist_share_needs_partner: 'errors.code.artistShareNeedsPartner',
  artist_share_whole_agency_only: 'errors.code.artistShareWholeAgencyOnly',
  artist_share_exceeds_giver: 'errors.code.artistShareExceedsGiver',
  artist_share_needs_edit: 'errors.code.artistShareNeedsEdit',
  artist_share_not_yours: 'errors.code.artistShareNotYours',
  artist_share_outside_organization: 'errors.code.artistShareOutsideOrganization',
  artist_share_parent_gone: 'errors.code.artistShareParentGone',
  artist_share_exists: 'errors.code.artistShareExists',
  artist_share_unknown_person: 'errors.code.artistShareUnknownPerson',
  artist_share_has_account: 'errors.code.artistShareHasAccount',
  artist_login_needs_email: 'errors.code.artistLoginNeedsEmail',
  contact_not_in_organization: 'errors.code.contactNotInOrganization',
  /* Artistsiden med hele artisten og «Kan endre» (1120). */
  artist_share_needs_economy: 'errors.code.artistShareNeedsEconomy',
  artist_member_exists: 'errors.code.artistMemberExists',
  artist_member_needs_name: 'errors.code.artistMemberNeedsName',
  artist_format_is_default: 'errors.code.artistFormatIsDefault',
  /* «Slett kontoen min» (§2.6): grunnen står i `details[0].code`. */
  confirm_mismatch: 'errors.code.confirmMismatch',
  /* 284.5 (1151): kontoen er allerede satt til sletting. */
  already_scheduled: 'errors.code.alreadyScheduled',
  sole_superadmin: 'errors.code.soleSuperadmin',
  platform_admin: 'errors.code.platformAdmin',
};

/** The one place an error becomes a translation key. */
export function errorKey(error: unknown): TKey {
  if (error instanceof ApiError) {
    const detail = error.details[0]?.code;
    if (detail !== undefined && DETAIL_MESSAGE[detail] !== undefined) return DETAIL_MESSAGE[detail];
    /*
     * QA15-S K3: en detaljkode med en tekst under `errors.code.<camelCase>` vises uten å stå i lista over.
     * Importens og malenes koder (`import_no_name_column`, `template_is_default`, `group_cycle` …) hadde
     * tekst i nb.json, men ble aldri slått opp, så brukeren fikk «Valideringen feilet» i stedet for hva som var galt.
     */
    if (detail !== undefined && /^[a-z0-9_]+$/u.test(detail)) {
      const key = errorCodeI18nKey(detail.toUpperCase() as ErrorCode);
      if (i18n.exists(key)) return dynamicKey(key);
    }
    return dynamicKey(errorCodeI18nKey(error.code));
  }
  if (error instanceof SupabaseAuthError) {
    return AUTH_MESSAGE[error.code] ?? 'errors.unexpected.description';
  }
  if (error instanceof NetworkError) return 'errors.network.description';
  if (error instanceof ContractViolationError) return 'errors.contract.description';
  return 'errors.unexpected.description';
}

/** The heading that belongs with `errorKey`. */
export function errorTitleKey(error: unknown): TKey {
  if (error instanceof NetworkError) return 'errors.network.title';
  if (error instanceof ContractViolationError) return 'errors.contract.title';
  // A refusal is not a malfunction. "Noe gikk galt" over a 403 invites the reader to retry
  // something that will never succeed; the heading has to say it was answered, not broken.
  if (isRefusal(error)) return 'errors.forbidden.title';
  return 'errors.unexpected.title';
}

/**
 * Was this an ANSWER rather than a malfunction?
 *
 * A 403 is the server having decided, and it will decide the same thing on every retry. What a
 * refusal needs is a way out, not a way to ask again — see `ErrorState`. Kept beside
 * `errorTitleKey`, which already makes the same distinction for the heading, so the two cannot
 * drift into disagreeing about what a 403 is.
 */
export function isRefusal(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

export function requestIdOf(error: unknown): string | null {
  return error instanceof ApiError && error.requestId.length > 0 ? error.requestId : null;
}

export function isApiErrorWithCode(error: unknown, code: ErrorCode): boolean {
  return error instanceof ApiError && error.code === code;
}

/** Narrows an unknown string from a JSON body onto the ErrorCode union. */
export function toErrorCode(value: unknown): ErrorCode {
  return isErrorCode(value) ? value : 'INTERNAL_ERROR';
}

/**
 * The `DenyReason` behind a 403, as a string.
 *
 * `PolicyDenied` crosses the wire as `FORBIDDEN` carrying one detail — `path: 'action'`,
 * `code: 'permission_denied'`, and a message shaped like
 * `"invitation:create: cannot_grant_higher_role"`
 * (`apps/api/src/common/errors/all-exceptions.filter.ts`). That trailing word is the only part a
 * user's sentence can be built from, and this is the one place it is dug out.
 *
 * Deliberately a plain string rather than the `DenyReason` union: narrowing it here would put a
 * second copy of the reason catalogue in the browser, and the caller compares against the one
 * reason it actually has copy for.
 */
export function policyDenyReason(error: unknown): string | null {
  if (!isRefusal(error) || !(error instanceof ApiError)) return null;
  const detail = error.details.find(
    (entry) => entry.path === 'action' && entry.code === 'permission_denied',
  );
  if (!detail) return null;
  const separator = detail.message.indexOf(': ');
  return separator === -1 ? null : detail.message.slice(separator + 2);
}

/**
 * FELTFEILENE FRA SERVEREN, PÅ FELTETS NAVN (A8, 22. oktober).
 *
 * Inndatapipa skriver hvor verdien kom fra foran stien (`body.email`, `query.limit`), mens en regel
 * i en tjeneste skriver feltet bart (`otherContactId`). Skjemaene kjenner bare feltnavnet, så
 * `setError('body.email')` traff ingenting, og brukeren så bare «Skjemaet har feil». Her fjernes
 * prefikset ett sted. Første kode per felt vinner, som i `fieldErrors`.
 */
const SOURCE_PREFIX = /^(body|query|param)\./;

export function serverFieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const out: Record<string, string> = {};
  for (const [path, code] of Object.entries(error.fieldErrors)) {
    const field = path.replace(SOURCE_PREFIX, '');
    if (field === 'body' || field === 'query' || field === 'param') continue;
    out[field] ??= code;
  }
  return out;
}

/** Does this failure carry a specific machine-readable detail code (§12.3)? */
export function hasDetailCode(error: unknown, code: string): boolean {
  return error instanceof ApiError && error.details.some((detail) => detail.code === code);
}
